import React, { useState, useEffect, useRef } from 'react';
import { ContactButtons, OptimizedImage } from '../common';

// Title-case words: "new york" → "New York"
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

function ProfileHeader({ therapist, onContactClick }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const contactButtonsRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 200;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatYearsExperience = (years) => {
    if (!years) return null;
    if (typeof years === 'string') return years;
    return `${years} years`;
  };

  const formatRate = (rate) => {
    if (!rate) return null;
    return `$${rate}/session`;
  };

  // Use actual therapist data without demo fallbacks
  const t = therapist;

  if (!t) return null;

  // Track phone contact with same system as ContactButtons
  const handlePhoneContact = async (phoneNumber) => {
    if (!phoneNumber) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(`${API_BASE_URL}/api/analytics/contact-interaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          therapistId: t._id,
          contactMethod: 'phone',
          timestamp: new Date().toISOString(),
          referrer: document.referrer || '',
          userAgent: navigator.userAgent,
          phoneNumber: 'REDACTED'
        }),
      });
    } catch (error) {
      console.error('Contact tracking error:', error);
    }

    // Create click-to-call link
    const telLink = `tel:${phoneNumber.replace(/[^\d+]/g, '')}`;
    window.location.href = telLink;
  };

  // Track website visit with same system as ContactButtons
  const handleWebsiteVisit = async (websiteUrl) => {
    if (!websiteUrl) return;

    // Ensure URL has protocol
    let formattedUrl = websiteUrl;
    if (!websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
      formattedUrl = `https://${websiteUrl}`;
    }

    try {
      // Add referral parameters for tracking
      const url = new URL(formattedUrl);
      url.searchParams.set('ref', 'therapylist');
      url.searchParams.set('therapist_id', t._id);
      url.searchParams.set('source', 'profile_header');
      url.searchParams.set('utm_source', 'therapylist');
      url.searchParams.set('utm_medium', 'referral');
      url.searchParams.set('utm_campaign', 'therapist_profile');

      // Track analytics
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(`${API_BASE_URL}/api/analytics/contact-interaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          therapistId: t._id,
          contactMethod: 'website',
          timestamp: new Date().toISOString(),
          referrer: document.referrer || '',
          userAgent: navigator.userAgent,
          originalUrl: websiteUrl,
          trackedUrl: url.toString()
        }),
      });

      // Open in new tab with tracking parameters
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Invalid website URL:', error);
      // Fallback: try opening the original URL
      window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Sticky condensed header
  const CondensedHeader = () => {
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
      const checkScreenSize = () => {
        setIsDesktop(window.innerWidth >= 1024);
      };
      
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
      
      return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    return (
      <header 
        className="fixed left-0 right-0 z-[80] pb-5 -mx-8" 
        style={{ 
          top: isDesktop 
            ? 'calc(var(--header-height, 85px) - 29px)' 
            : 'calc(var(--header-height, 85px) - 4px)'
        }}
      >
      <div className="px-8">
        <div className="mx-auto max-w-7xl">
          <div className="bg-white rounded-b-2xl px-8 py-4 border border-gray-100 transition-all duration-300 ease-out shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <OptimizedImage
                    src={t.profilePhoto} 
                    alt={`${t.firstName} ${t.lastName}`}
                    className="w-12 h-12 rounded-full"
                    loading="eager"
                    sizes="48px"
                    fallbackIcon={
                      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    }
                  />
                  {t.verification?.isVerified && (
                    <div className="absolute -top-1 -right-1 bg-[#254e70] text-white p-1 rounded-full">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit, sans-serif' }}>
                    {t.firstName} {t.lastName}
                    {t.pronouns && (
                      <span className="text-sm font-normal text-[#254e70]/70 ml-2">({t.pronouns})</span>
                    )}
                  </h2>
                  {t.location?.city && t.location?.state && (
                    <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                      {capitalizeWords(t.location.city)}, {t.location.state}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Quick Actions for Sticky Header */}
                {t.phone && (
                  <button
                    onClick={() => handlePhoneContact(t.phone)}
                    className="bg-[#9dacff] text-white px-2 py-1.5 rounded-lg font-medium hover:bg-[#8a9aef] transition-all duration-300 flex items-center gap-1"
                    title={`Call ${t.phone}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </button>
                )}
                {t.website && (
                  <button
                    onClick={() => handleWebsiteVisit(t.website)}
                    className="bg-[#9dacff] text-white px-2 py-1.5 rounded-lg font-medium hover:bg-[#8a9aef] transition-all duration-300 flex items-center gap-1"
                    title={`Visit ${t.firstName}'s website`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => contactButtonsRef.current?.openModal()}
                  className="bg-[#254e70] text-white px-3 py-1.5 rounded-lg font-medium hover:bg-[#1d3e5a] transition-all duration-300 flex items-center gap-1.5"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
    );
  };

  return (
    <>
      {isScrolled && <CondensedHeader />}
      
      {/* Compact, Wide Dashboard Style */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#254e70]/10 mb-6 max-w-7xl mx-auto overflow-hidden">
        
        {/* Background Pattern */}
        <div className="relative bg-gradient-to-r from-[#254e70]/3 via-transparent to-[#9dacff]/3">
          
          <div className="p-8">
            <div className="grid grid-cols-12 gap-8 items-center">
              
              {/* Photo - Compact */}
              <div className="col-span-12 lg:col-span-2">
                <div className="relative w-40 h-48 mx-auto lg:mx-0">
                  <OptimizedImage
                    src={t.profilePhoto} 
                    alt={`${t.firstName} ${t.lastName}`}
                    className="w-full h-full rounded-xl shadow-md"
                    loading="eager"
                    sizes="(max-width: 768px) 160px, 160px"
                    fallbackIcon={
                      <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    }
                  />
                  {t.verification?.isVerified && (
                    <div className="absolute -top-2 -right-2 bg-[#254e70] text-white p-1.5 rounded-full shadow-lg border-2 border-white">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Info - Wide */}
              <div className="col-span-12 lg:col-span-6 text-center lg:text-left">
                {/* Name & Title */}
                <div className="mb-4">
                  <h1 className="text-3xl lg:text-4xl font-bold text-[#254e70] mb-1 leading-tight" style={{ fontFamily: 'Kanit, sans-serif' }}>
                    {t.firstName} {t.lastName}
                    {t.pronouns && (
                      <span className="text-lg lg:text-xl font-normal text-[#254e70]/70 ml-3">({t.pronouns})</span>
                    )}
                  </h1>
                  <p className="text-lg text-gray-600 mb-1" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                    {t.title || t.professionalType || 'Licensed Therapist'}
                  </p>
                  {t.practiceName && (
                    <p className="text-base text-[#9dacff] font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                      {t.practiceName}
                    </p>
                  )}
                </div>

                {/* Key Stats - Inline */}
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start mb-4 text-sm">
                  {t.location?.city && t.location?.state && (
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span className="text-gray-700 font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        {capitalizeWords(t.location.city)}, {t.location.state}
                      </span>
                    </div>
                  )}
                  
                  {formatYearsExperience(t.professional?.yearsExperience) && (
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700 font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        {formatYearsExperience(t.professional.yearsExperience)}
                      </span>
                    </div>
                  )}

                  {formatRate(t.rates?.individual) && (
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      <span className="text-gray-700 font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        {formatRate(t.rates.individual)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Specializations - Compact */}
                {t.specializations && t.specializations.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                    {t.specializations.slice(0, 6).map((specialty, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-[#e4fde1] text-[#254e70] rounded-full text-sm font-medium border border-[#254e70]/20"
                        style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                      >
                        {typeof specialty === 'object' ? specialty.name : specialty}
                      </span>
                    ))}
                    {t.specializations.length > 6 && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                        +{t.specializations.length - 6} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Status & Contact - Right Side */}
              <div className="col-span-12 lg:col-span-4 text-center lg:text-right flex flex-col h-full">
                
                {/* Status Badges - Top */}
                <div className="flex flex-wrap gap-2 justify-center lg:justify-end mb-4">
                  {t.acceptingNewClients !== false && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#e4fde1] text-[#254e70] rounded-full text-sm font-medium border border-[#254e70]/20">
                      <div className="w-1.5 h-1.5 bg-[#254e70] rounded-full animate-pulse"></div>
                      Accepting clients
                    </span>
                  )}

                  {t.rates?.freeConsultation?.offered && (
                    <span className="px-3 py-1.5 bg-[#9dacff] text-white rounded-full text-sm font-medium">
                      Free consultation
                    </span>
                  )}
                </div>

                {/* Quick Info - Middle */}
                <div className="flex-1 flex items-center justify-center lg:justify-end">
                  {t.professional?.languages && t.professional.languages.length > 1 && (
                    <div className="text-sm text-gray-600">
                      <p style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        <span className="font-medium">Languages:</span> {t.professional.languages.join(', ')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Contact Buttons - Bottom */}
                <div className="flex justify-center lg:justify-end mt-auto">
                  <div className="w-full max-w-sm">
                    <ContactButtons 
                      ref={contactButtonsRef}
                      therapist={t} 
                      onContactClick={onContactClick}
                      className="space-y-2"
                      size="compact"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProfileHeader;